import { Mutation } from '@monolith/legacy/store/mutation-types';
import { captureException } from '@core/plugins/sentry';

import account from '@monolith/legacy/store/account';
import legacyAccount from '@monolith/legacy/store/legacy-account';
import cart, { CART_STORE_ID } from '@monolith/legacy/store/cart';
import offers, { OFFERS_STORE_ID } from '@monolith/legacy/store/offers';
import shop, { SHOP_STORE_ID } from '@monolith/legacy/store/shop';
import env from '@monolith/legacy/store/env';
import conversations from '@monolith/legacy/store/conversations';
import orderRetailerRejection from '@monolith/legacy/store/order-retailer-rejection';
import salesforce from '@monolith/legacy/store/salesforce';
import global from '@monolith/legacy/store/global';
import wishlist from '@monolith/legacy/store/wishlist';
import headerBadges from '@monolith/legacy/store/headerBadges';
import wallet from '@monolith/legacy/store/wallet';
import brandDashboardStore from '@bc/advertisement/feature/ads-dashboard/store/index';

import drawer from '@monolith/legacy/store/drawer';
import reviews from '@monolith/legacy/store/reviews';
import discountInfo from '@monolith/legacy/store/discount-info';
import signUp from '@monolith/legacy/store/sign-up';
import tracking from '@monolith/legacy/store/tracking';

import ProductUnlikedEvent from '@monolith/legacy/services/analytics/events/product-unliked-event';
import Analytics from '@monolith/legacy/services/analytics';
import BrandUnlikedEvent from '@monolith/legacy/services/analytics/events/brand-unliked-event';
import Category, { Categories } from '@monolith/legacy/types/category';
import Dinero, { Currency } from 'dinero.js';
import { seeYouSoonOfferMinimumBrands } from '@monolith/legacy/mixins/promocode';
import Product from '@monolith/legacy/types/product';
import { CurrencyRates, State } from './state';
import { getInitialState } from '@modules/core/services/initial-state';
import { mutations as mutationsModule } from './mutations';
import { Retailer } from '@monolith/legacy/types/api/retailer';
import UserClick from '@monolith/legacy/services/analytics/events/user-click.event';
import { Brand } from '@monolith/legacy/types/api/brand';
import http from '@monolith/legacy/services/api/http';
import { getMe } from '@monolith/legacy/services/api/me';
import {
  fetchMeConfig,
  fetchCurrenciesRates,
  fetchGlobalConfigData,
  fetchCategories,
} from '@modules/core/services/apiInitialState';
import { isMetaTagMigrationEnabled, setInjectedContent } from '@modules/core/services/injected-content';
import { GlobalMetaTagName, StateMetaTagName } from '@modules/core/services/initial-state';
import { AdditionalLikeTrackingData } from '@monolith/legacy/types/analytics/recommendation-tracking';
import { UserType } from '@monolith/legacy/types/brand-onboarding';
import { User } from '@monolith/legacy/types/user';
import { InvitedStatusValue } from '@monolith/legacy/types/r2b';
import { createStore } from 'vuex';
import { getCurrency } from '@modules/core/services/metas/currency';

const pendingProductFetches = new Map<number, Promise<Product>>();

const state: State = {
  categories: {
    entities: {},
    order: [],
  },
  products: {},
  staff: false,
  isSignupShown: false,
  user: null,
  retailer: null,
  stripe: null,
  progressBar: 'hide',
  previousScrollY: 0,
  likes: {
    productIds: null,
    brandIds: null,
  },
  offers: {
    events: [],
  },
  country: null,
  brand: null,
  currency: null,
  currenciesRates: null,
  showRequestedInformationForm: false,
  measurementUnits: {
    fetching: false,
    unitList: [],
  },
  ...getInitialState(),
};
if (state.user) {
  const lastProductLikesDate = parseInt(localStorage.getItem(state.user.id + '/lastProductLikesDate'), 10);
  const productLikes = localStorage.getItem(state.user.id + '/productLikes');
  if (lastProductLikesDate && productLikes && Date.now() - lastProductLikesDate < 86400) {
    state.likes.productIds = JSON.parse(productLikes);
  }

  const lastBrandLikesDate = parseInt(localStorage.getItem(state.user.id + '/lastBrandLikesDate'), 10);
  const brandLikes = localStorage.getItem(state.user.id + '/brandLikes');
  if (lastBrandLikesDate && brandLikes && Date.now() - lastBrandLikesDate < 86400) {
    state.likes.brandIds = JSON.parse(brandLikes);
  }
}

state.staff = document.cookie.split(';').some((item) => item.trim().includes('ankorStaff=1'));

export const actions = {
  clearBrandPendingAction({ dispatch, state }, categorie: string) {
    return dispatch('updateBrand', {
      brand: {
        ...state.brand,
        pending_actions: {
          ...Object.fromEntries(Object.entries(state.brand.pending_actions).filter(([key]) => key !== categorie)),
        },
      },
    });
  },
  updateBrand({ commit }, { brand }) {
    commit('REPLACE_BRAND', brand);
  },
  /**
   * Hydrate all migrated meta tag states from API before app mounts.
   * Calls public and authenticated APIs in parallel for performance.
   * Authenticated APIs are only called if user is authenticated (prevents 401 redirect loops for guests).
   */
  async fetchStateFromApi({ commit }, { isAuthenticated = true } = {}) {
    try {
      const isCountryMigrationEnabled = isMetaTagMigrationEnabled(StateMetaTagName.Country);
      const isCurrencyMigrationEnabled = isMetaTagMigrationEnabled(StateMetaTagName.Currency);
      const isCurrenciesRatesMigrationEnabled = isMetaTagMigrationEnabled(StateMetaTagName.CurrenciesRates);
      const isManagedLanguagesMigrationEnabled = isMetaTagMigrationEnabled(GlobalMetaTagName.ManagedLanguages);
      const isGlobalConfigMigrationEnabled = isMetaTagMigrationEnabled(GlobalMetaTagName.GlobalConfig);
      const isCdnMigrationEnabled = isMetaTagMigrationEnabled(GlobalMetaTagName.CDN);
      const isBrowserIdMigrationEnabled = isMetaTagMigrationEnabled(GlobalMetaTagName.BrowserId);
      const isCategoriesMigrationEnabled = isMetaTagMigrationEnabled(GlobalMetaTagName.Categories);
      const isTagsMigrationEnabled = isMetaTagMigrationEnabled(GlobalMetaTagName.Tags);
      const isSentryDsnMigrationEnabled = isMetaTagMigrationEnabled(GlobalMetaTagName.SentryDSN);
      const isEnvironmentMigrationEnabled = isMetaTagMigrationEnabled(GlobalMetaTagName.Environment);

      // Single API call for managedLanguages, globalConfig, tags, CDN, sentryDsn, and environment (same endpoint)
      const needsGlobalConfigData =
        isManagedLanguagesMigrationEnabled ||
        isGlobalConfigMigrationEnabled ||
        isTagsMigrationEnabled ||
        isCdnMigrationEnabled ||
        isSentryDsnMigrationEnabled ||
        isEnvironmentMigrationEnabled;

      // Prepare promises for parallel execution
      const publicPromises = Promise.all([
        isCurrenciesRatesMigrationEnabled ? fetchCurrenciesRates() : Promise.resolve(null),
        needsGlobalConfigData
          ? fetchGlobalConfigData()
          : Promise.resolve({ globalConfig: null, managedLanguages: null, imgCdn: null, sentryDsn: null, environment: null }),
        isCategoriesMigrationEnabled ? fetchCategories() : Promise.resolve(null),
      ]);

      const authPromise = isAuthenticated
        ? Promise.all([
            getMe(),
            isCountryMigrationEnabled || isCurrencyMigrationEnabled || isBrowserIdMigrationEnabled
              ? fetchMeConfig()
              : Promise.resolve(null),
          ])
        : Promise.resolve([null, null] as const);

      // Execute all API calls in parallel for better performance
      const [[currenciesRatesResponse, globalConfigDataResponse, categoriesResponse], [meResponse, meConfigResponse]] =
        await Promise.all([publicPromises, authPromise]);

      const {
        globalConfig: globalConfigResponse,
        managedLanguages: managedLanguagesResponse,
        imgCdn: imgCdnResponse,
        sentryDsn: sentryDsnResponse,
        environment: environmentResponse,
      } = globalConfigDataResponse;

      if (meResponse?.brand && isMetaTagMigrationEnabled(StateMetaTagName.Brand)) {
        commit(Mutation.REPLACE_BRAND, meResponse.brand);
      }

      if (meResponse?.retailer && isMetaTagMigrationEnabled(StateMetaTagName.Retailer)) {
        commit(Mutation.REPLACE_RETAILER, meResponse.retailer);
      }

      if (meResponse?.stripe && isMetaTagMigrationEnabled(StateMetaTagName.Stripe)) {
        commit(Mutation.REPLACE_STRIPE, meResponse.stripe);
      }

      if (meResponse && isMetaTagMigrationEnabled(StateMetaTagName.User)) {
        const user = {
          id: meResponse.id,
          uuid: meResponse.uuid,
          email: meResponse.email,
          first_name: meResponse.first_name,
          last_name: meResponse.last_name,
          created_at: meResponse.created_at ?? '',
          updated_at: meResponse.updated_at ?? '',
          business: {
            ...meResponse.business,
            brand: meResponse.brand,
            retailer: meResponse.retailer,
          },
          business_id: meResponse.business?.id ?? 0,
          locale: '',
          disabled: false,
          email_verified_at: null,
          deleted_at: null,
        };
        commit(Mutation.REPLACE_USER, user);
        setInjectedContent(StateMetaTagName.User, user);
      }

      if (meResponse && isMetaTagMigrationEnabled(StateMetaTagName.ShowRequestedInformationForm)) {
        commit(Mutation.REPLACE_SHOW_REQUESTED_INFORMATION_FORM, meResponse.show_requested_information_form ?? false);
      }

      // Country/currency/browserId: commit to store + setInjectedContent for backward compat
      // (getCurrency() and $currency global property read from injected content)
      // (getBrowserId() reads from injected content)
      if (meConfigResponse) {
        const { country, currency, browserId } = meConfigResponse;

        if (country && isCountryMigrationEnabled) {
          commit(Mutation.REPLACE_COUNTRY, country);
          setInjectedContent(StateMetaTagName.Country, country);
        }
        if (currency && isCurrencyMigrationEnabled) {
          commit(Mutation.REPLACE_CURRENCY, currency);
          setInjectedContent(StateMetaTagName.Currency, currency);
        }
        if (browserId && isBrowserIdMigrationEnabled) {
          setInjectedContent(GlobalMetaTagName.BrowserId, browserId);
        }
      }

      // CurrenciesRates: commit to store + setInjectedContent for backward compat
      // ($currenciesRates global property reads from injected content)
      if (currenciesRatesResponse && isCurrenciesRatesMigrationEnabled) {
        const { date, rates } = currenciesRatesResponse;
        if (date && rates) {
          const currenciesRates = { date, rates };
          commit(Mutation.REPLACE_CURRENCIES_RATES, currenciesRates);
          setInjectedContent(StateMetaTagName.CurrenciesRates, currenciesRates);
        }
      }

      // ManagedLanguages: setInjectedContent for backward compat
      if (managedLanguagesResponse && isManagedLanguagesMigrationEnabled) {
        setInjectedContent(GlobalMetaTagName.ManagedLanguages, managedLanguagesResponse);
      }

      // CDN: setInjectedContent for backward compat
      if (imgCdnResponse && isCdnMigrationEnabled) {
        setInjectedContent(GlobalMetaTagName.CDN, imgCdnResponse);
      }

      // GlobalConfig: setInjectedContent for backward compat
      // (getGlobalConfig() reads from injected content)
      if (globalConfigResponse && isGlobalConfigMigrationEnabled) {
        setInjectedContent(GlobalMetaTagName.GlobalConfig, globalConfigResponse);
      }

      // Categories: setInjectedContent for backward compat
      // (getCategories() and $categories global property read from injected content)
      if (categoriesResponse && isCategoriesMigrationEnabled) {
        setInjectedContent(GlobalMetaTagName.Categories, categoriesResponse);
      }

      // Tags: extract from globalConfig response and setInjectedContent for backward compat
      // (getTags() and $tags global property read from injected content)
      const tagsResponse = globalConfigResponse?.tags;
      if (Array.isArray(tagsResponse) && isTagsMigrationEnabled) {
        setInjectedContent(GlobalMetaTagName.Tags, tagsResponse);
      }

      // SentryDSN: setInjectedContent for backward compat
      // (initSentry reads DSN from injected content via getInjectedContent)
      if (sentryDsnResponse && isSentryDsnMigrationEnabled) {
        setInjectedContent(GlobalMetaTagName.SentryDSN, sentryDsnResponse);
      }

      // Environment: setInjectedContent for backward compat
      // (Sentry init and ARIA embed read from injected content)
      if (environmentResponse && isEnvironmentMigrationEnabled) {
        setInjectedContent(GlobalMetaTagName.Environment, environmentResponse);
      }
    } catch (error) {
      captureException(error);
    }
  },
  async likeProduct(
    { commit, getters, dispatch },
    {
      id,
      additionalTrackingData,
      disableTracking,
    }: { id: number; additionalTrackingData?: AdditionalLikeTrackingData; disableTracking?: boolean }
  ) {
    if (!getters.user) {
      commit(Mutation.REPLACE_PRODUCT_LIKES, []);
      return;
    }

    commit(Mutation.ADD_PRODUCT_LIKE, id);

    try {
      await dispatch('wishlist/products/addLikedProductToDefaultWishlist', { products: [id] });
    } catch {
      await dispatch('fetchProductLikes');
    }

    if (disableTracking) {
      return;
    }

    let trackingData = {
      id,
      component: null,
      action: 'product_liked',
      item_type: 'product',
    };
    if (additionalTrackingData) {
      trackingData = { ...trackingData, ...additionalTrackingData };
    }

    await Analytics.track(new UserClick(trackingData));
  },
  async unLikeProduct(
    { commit, getters, dispatch },
    { id, sectionId, disableTracking }: { id: number; sectionId?: string; disableTracking?: boolean }
  ) {
    if (!getters.user) {
      commit(Mutation.REPLACE_PRODUCT_LIKES, []);
      return;
    }

    commit(Mutation.REMOVE_PRODUCT_LIKE, id);

    try {
      await dispatch('wishlist/products/removeUnlikedProductFromWishlist', { productId: id });
    } catch {
      await dispatch('fetchProductLikes');
    }

    const product = getters.productById(id);
    if (disableTracking || !product) {
      return;
    }
    await Analytics.track(
      new ProductUnlikedEvent(
        {
          id,
          name: product.name,
          wholesalePrice: product.wholesale_price,
          uuid: product.uuid,
        },
        sectionId
      )
    );
  },
  fetchProductLikes({ commit, getters }) {
    if (!getters.user || !getters.userIsRetailer) {
      commit(Mutation.REPLACE_PRODUCT_LIKES, []);
      return Promise.resolve();
    }
    return http()
      .get(`/api/wishlists/all/products`, {
        noProgressBar: true,
      })
      .then(({ data }) => {
        commit(Mutation.REPLACE_PRODUCT_LIKES, data.data);
      });
  },
  async likeBrand(
    { commit, getters },
    { id, sectionId, disableTracking }: { id: number; sectionId?: string; disableTracking?: boolean }
  ): Promise<void> | null {
    if (!getters.user) {
      commit(Mutation.REPLACE_BRAND_LIKES, []);
      return null;
    }
    await http().put(`/api/likes/brand/${id}`);
    commit(Mutation.ADD_BRAND_LIKE, id);

    if (disableTracking) {
      return;
    }

    const trackingData = {
      id,
      component: null,
      action: 'brand_liked',
    };
    if (sectionId) {
      trackingData['id_section'] = sectionId;
    }
    await Analytics.track(new UserClick(trackingData));
  },
  async unLikeBrand(
    { commit, getters },
    { id, sectionId, disableTracking }: { id: number; sectionId?: string; disableTracking?: boolean }
  ) {
    if (!getters.user) {
      commit(Mutation.REPLACE_BRAND_LIKES, []);
      return;
    }
    await http().delete(`/api/likes/brand/${id}`);
    commit(Mutation.REMOVE_BRAND_LIKE, id);

    if (disableTracking) {
      return;
    }
    await Analytics.track(new BrandUnlikedEvent({ id }, sectionId));
  },
  fetchBrandLikes({ commit, getters }) {
    if (!getters.user) {
      commit(Mutation.REPLACE_BRAND_LIKES, []);
      return;
    }
    http()
      .get(`/api/likes/brand`)
      .then(({ data }) => {
        commit(Mutation.REPLACE_BRAND_LIKES, data.data);
      });
  },
  fetchProduct({ commit }, { product_id }): Promise<Product> {
    let pending = pendingProductFetches.get(product_id);
    if (!pending) {
      pending = (async () => {
        const {
          data: { data: product },
        } = await http().get(`/api/products/${product_id}`);

        commit(Mutation.STORE_PRODUCT, {
          ...product,
          refreshed: true,
        });

        return product;
      })();
      pendingProductFetches.set(product_id, pending);
      pending.finally(() => pendingProductFetches.delete(product_id)).catch(() => {});
    }

    return pending;
  },
  async storeProductsFromAlgolia({ commit, getters }, { products }) {
    products = await Promise.all(
      products.map(async (product) => {
        product.original_wholesale_price.amount = Math.round(product.original_wholesale_price.amount);
        product.wholesale_price.amount = Math.round(product.wholesale_price.amount);
        product.retail_price.amount = Math.round(product.retail_price.amount);
        const wholeSalePriceConverted = await Dinero(product.wholesale_price).convert(getCurrency(), {
          endpoint: new Promise((resolve) =>
            resolve(getters.getCurrencyRate(product.original_wholesale_price.currency, getCurrency()))
          ),
        });
        const originalWholeSalePriceConverted = await Dinero(product.original_wholesale_price).convert(getCurrency(), {
          endpoint: new Promise((resolve) => resolve(getters.getCurrencyRate(product.wholesale_price.currency, getCurrency()))),
        });
        const retailPriceConverted = await Dinero(product.retail_price).convert(getCurrency(), {
          endpoint: new Promise((resolve) => resolve(getters.getCurrencyRate(product.retail_price.currency, getCurrency()))),
        });
        return {
          ...product,
          wholesale_price: wholeSalePriceConverted.toObject(),
          original_wholesale_price: originalWholeSalePriceConverted.toObject(),
          retail_price: retailPriceConverted.toObject(),
        };
      })
    );

    commit(
      Mutation.STORE_PRODUCTS,
      products.map((product) => ({ ...product, refreshed: false }))
    );
  },
  replaceRetailer({ commit }, retailer) {
    commit(Mutation.REPLACE_RETAILER, retailer);
  },
  replaceBrand({ commit }, { brand }) {
    commit(Mutation.REPLACE_BRAND, brand);
  },
  showProgressBar({ commit }) {
    commit('SET_PROGRESS_BAR_STATE', 'loading');
  },
  endProgressBar({ commit }) {
    commit('SET_PROGRESS_BAR_STATE', 'ending');
  },
  hideProgressBar({ commit }) {
    commit('SET_PROGRESS_BAR_STATE', 'hide');
  },
  async toggleMonthlyFollowup({ commit }, monthly_follow_up: boolean): Promise<void> {
    try {
      commit('SET_BRAND_MONTHLY_FOLLOWUP', monthly_follow_up);
      await http().post(`/api/me/brand/referral/monthly-follow-up`, {
        monthly_follow_up,
      });
      return Promise.resolve();
    } catch (_error) {
      commit('SET_BRAND_MONTHLY_FOLLOWUP', !monthly_follow_up);
      return Promise.reject();
    }
  },
  hideRequestedInformation({ commit }) {
    commit('HIDE_REQUESTED_INFORMATION');
  },
  showSignup({ commit }) {
    commit('SHOW_SIGNUP');
  },
};

export const getters = {
  getCurrencyRate:
    (state: State) =>
    (from: Currency, to: Currency): CurrencyRates => ({
      date: state.currenciesRates.date,
      rates: {
        [to]: from !== to ? state.currenciesRates.rates[from][to] : 1,
      },
    }),
  isStaff: (state: State): boolean => state.staff,
  isSignupShown: (state: State): boolean => state.isSignupShown,
  retailer: (state: State): Retailer => state.retailer,
  brand: (state: State): Brand => state.brand,
  isPartiallyCompleted: (state: State): boolean => state.brand?.partially_completed ?? false,
  user: (state: State): User => state.user,
  isUserAuthenticated: (state: State): boolean => Boolean(state.user),
  getUserType: (state: State): UserType => {
    if (!state.user) {
      return 'guest';
    }
    if (state.brand) {
      return 'brand';
    }
    return 'retailer';
  },
  shouldApplyCountryRestrictions: (_state, getters) =>
    // we apply country restrictions only if
    // 1. the user is not logged-in
    (!getters.user ||
      // 2. or the user is a retailer in an opened-to-buy country
      (getters.userIsRetailer && getters.retailer.country.opened_to_retailers)) &&
    // 3 and is not a beloved colleague
    !getters.isStaff,
  stripe: (state: State): any => state.stripe,
  hasPendingActions:
    (state: State) =>
    (categories: any[]): boolean => {
      let pendingActions = {};

      if (state.brand) {
        pendingActions = state.brand.pending_actions;
      } else if (state.retailer) {
        pendingActions = state.retailer.pending_actions;
      }

      if (categories.length === 0) {
        // If the category list is empty, we're check for any action categories
        return Object.keys(pendingActions).length > 0;
      }

      // else, the function wll return true as soon as one given category matches in the pending actions
      return categories.some((category) => {
        return Object.prototype.hasOwnProperty.call(pendingActions, category);
      });
    },
  productLikesAreInitialized: (state: State): boolean => state.likes.productIds !== null,
  brandLikesAreInitialized: (state: State): boolean => state.likes.brandIds !== null,
  userIsRetailer: (state: State): boolean => !!state.retailer,
  userIsBrand: (state: State): boolean => !!state.brand,
  userIsCandidateBrand: (state: State): boolean =>
    !!state.user?.business?.brand?.candidate_brand &&
    state.user?.business?.brand?.candidate_brand.status != InvitedStatusValue.Accepted,
  canGetWholesalePrice: (state: State): boolean => Boolean(state.retailer?.can_get_wholesale_price),
  hasUnhandledCountry: (state: State): boolean => state.retailer && !state.retailer.country.opened_to_retailers,
  productIsLiked:
    (state: State) =>
    (productId: number): boolean =>
      state.likes.productIds && state.likes.productIds.includes(productId),
  getLikedBrands: (state: State): number[] => state.likes.brandIds,
  brandIsLiked:
    (state: State) =>
    (brandId: number): boolean =>
      state.likes.brandIds && state.likes.brandIds.includes(brandId),
  productById:
    (state: State) =>
    (id: number): Product =>
      state.products[id],
  productByOptionId:
    (state: State) =>
    (id: number): any =>
      Object.values(state.products).find((product: any) => product.options.find((option) => id == option.id)),
  productByVariantUuid:
    (state: State) =>
    (uuid: string): any =>
      Object.values(state.products).find((product: any) => product.variants.find((variant) => uuid == variant.uuid)),
  userCountry: (state: State): string => (state.user ? state.user.business?.country?.iso_code : state.country),
  userCreationDate: (state: State): string => state.user?.created_at,
  userCurrency: (state: State): any => state.currency,
  currencyRates: (state: State): CurrencyRates => state.currenciesRates,
  getSiblingCategories:
    (state: State) =>
    (parentId: number | null): Category[] =>
      state.categories.order.map((id) => state.categories.entities[id]).filter((category) => category.parent_id === parentId),

  getEligiblesEvents: (state: State): any[] => state.brand.business_events.filter(({ is_eligible }) => is_eligible),
  getNextEligibleEvent: (state: State) => state.brand.business_events.find(({ is_eligible }) => is_eligible),
  retailerNeedsMoreBrandsToUseSeeYouSoonOffer: (_state, _getters, _rootState, rootGetters) => {
    return (
      rootGetters.retailer?.see_you_soon_promocode_active && rootGetters['cart/aboveCarts']?.length < seeYouSoonOfferMinimumBrands
    );
  },
  hasRetailerAlreadyOrdered: (state: State): boolean => state.retailer?.has_already_ordered,
};

export const mutations = {
  [Mutation.REPLACE_PRODUCT_LIKES](state, likes): void {
    state.likes.productIds = likes;
  },
  [Mutation.ADD_PRODUCT_LIKE](state, id): void {
    state.likes.productIds.push(id);
  },
  [Mutation.REMOVE_PRODUCT_LIKE](state, id): void {
    state.likes.productIds = state.likes.productIds.filter((value) => id != value);
  },
  [Mutation.REPLACE_BRAND_LIKES](state, likes): void {
    state.likes.brandIds = likes;
  },
  [Mutation.ADD_BRAND_LIKE](state, id): void {
    state.likes.brandIds.push(id);
  },
  [Mutation.REMOVE_BRAND_LIKE](state, id): void {
    state.likes.brandIds = state.likes.brandIds.filter((value) => id != value);
  },
  [Mutation.STORE_PRODUCT](state, product): void {
    // https://vuejs.org/v2/guide/list.html#Caveats
    // Vue.set(state.products, product.id, product);
    state.products[product.id] = product;
  },
  [Mutation.STORE_PRODUCTS](state, products): void {
    state.products = {
      ...state.products,
      ...Object.fromEntries(
        products
          .filter(
            // Do not updated a refreshed product with a non-refreshed
            (product) => product.refreshed || !state.products[product.id]?.refreshed
          )
          .map((product) => [product.id, product])
      ),
    };
  },
  [Mutation.REPLACE_RETAILER](state, retailer): void {
    state.retailer = retailer;
  },
  [Mutation.REPLACE_USER](state, user): void {
    state.user = user;
  },
  [Mutation.REPLACE_BRAND](state, brand): void {
    state.brand = brand;
  },
  [Mutation.REPLACE_COUNTRY](state, country): void {
    state.country = country;
  },
  [Mutation.REPLACE_CURRENCY](state, currency): void {
    state.currency = currency;
  },
  [Mutation.REPLACE_CURRENCIES_RATES](state: State, currenciesRates: CurrencyRates): void {
    state.currenciesRates = currenciesRates;
  },
  SET_PROGRESS_BAR_STATE(state, value): void {
    state.progressBar = value;
  },
  [Mutation.SET_CATEGORIES](state, { categories, order }: { categories: Categories; order: string[] }): void {
    state.categories.entities = { ...categories };
    state.categories.order = [...order];
  },
  SET_BRAND_MONTHLY_FOLLOWUP(state, isEnabled: boolean) {
    state.brand.monthly_follow_up = isEnabled;
  },
  [Mutation.REPLACE_STRIPE](state, stripe): void {
    state.stripe = stripe;
  },
  [Mutation.HIDE_REQUESTED_INFORMATION](state): void {
    state.showRequestedInformationForm = false;
  },
  [Mutation.SHOW_SIGNUP](state): void {
    state.isSignupShown = true;
  },
  [Mutation.REPLACE_SHOW_REQUESTED_INFORMATION_FORM](state, value: boolean): void {
    state.showRequestedInformationForm = value;
  },
  ...mutationsModule,
};

const store = createStore({
  state,
  strict: process.env.NODE_ENV !== 'production',
  modules: {
    headerBadges,
    account,
    legacyAccount,
    [CART_STORE_ID]: cart,
    [OFFERS_STORE_ID]: offers,
    env,
    [SHOP_STORE_ID]: shop,
    conversations,
    orderRetailerRejection,
    salesforce,
    global,
    wishlist,
    wallet,
    brandDashboardStore,
    drawer,
    reviews,
    discountInfo,
    signUp,
    tracking,
  },
  mutations,
  getters,
  actions,
});

store.subscribe((mutation, state) => {
  if (
    Mutation.REPLACE_PRODUCT_LIKES === mutation.type ||
    Mutation.ADD_PRODUCT_LIKE === mutation.type ||
    Mutation.REMOVE_PRODUCT_LIKE === mutation.type
  ) {
    if (!state.user) {
      return;
    }
    localStorage.setItem(state.user.id + '/lastProductLikesDate', Date.now().toString());
    localStorage.setItem(state.user.id + '/productLikes', JSON.stringify(state.likes.productIds));
  }
  if (
    Mutation.REPLACE_BRAND_LIKES === mutation.type ||
    Mutation.ADD_BRAND_LIKE === mutation.type ||
    Mutation.REMOVE_BRAND_LIKE === mutation.type
  ) {
    if (!state.user) {
      return;
    }
    localStorage.setItem(state.user.id + '/lastBrandLikesDate', Date.now().toString());
    localStorage.setItem(state.user.id + '/brandLikes', JSON.stringify(state.likes.brandIds));
  }
});

export default store;
export type RootState = State;
export type RootGetters = typeof getters;
export type RootActions = typeof actions;
export type RootMutations = typeof mutations;
